// Блок раздела: заголовок, необязательное действие в его шапке, содержимое.
//
// Заведён 25.08.2026 по снимкам сводки. Там блоки были набраны вручную: заголовок обычным
// текстом, а кнопка «Выгрузить в Excel» — отдельным элементом, который сам прижимался к
// правому краю. На экране это выглядело как три одинаковые кнопки, висящие в пустоте на
// разной высоте, без видимой связи с тем, что они выгружают. Здесь действие принадлежит
// заголовку и стоит на его линии — видно, что выгружается именно этот блок.
//
// У блока есть рамка и белый фон. На сером поле раздела это единственный способ показать
// границу смысла: до этого блоки различались только вертикальным зазором, и человек не мог
// сказать, относится ли строка списка к заголовку сверху или к следующему.

import type { RemixiconComponentType } from '@remixicon/react';

import { forwardRef } from 'react';
import type { ReactNode } from 'react';

import { cn } from '@/utils/cn';

export interface BlockProps {
  title: string;
  /**
   * Значок слева от заголовка: чем этот блок отличается от соседнего при беглом взгляде.
   *
   * Блоки на экране идут подряд и различаются только словами заголовка. Пока глаз читает
   * слова, экран из пяти блоков разбирается сверху вниз, по строчке за раз; значок даёт
   * зацепку, по которой нужный блок находится не читая — так устроены блоки в AlignUI, и
   * ровно этой зацепки нашим не хватало.
   *
   * Необязателен: блоку без осмысленного значка лучше остаться без него, чем получить
   * случайный. Значок, не добавляющий смысла, — шум, который делает соседние блоки похожими.
   *
   * Тип тот же, что у пустого состояния, окна и поля ввода: набор значков в продукте один
   * (Remixicon), и своя подпись здесь означала бы, что блоку нельзя передать тот же значок,
   * который принимают все соседние компоненты.
   */
  icon?: RemixiconComponentType;
  /** Короткое пояснение под заголовком: что именно тут показано. */
  note?: ReactNode;
  /** Действие над блоком: выгрузка, переход в разрез. Стоит на линии заголовка. */
  action?: ReactNode;
  /** Убрать внутренние отступы: содержимое само знает, как лечь (списки, таблицы). */
  flush?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Ссылка на сам блок пробрасывается через `forwardRef`, а НЕ приходит обычным свойством.
 *
 * ⚠️ На React 18 свойство с именем `ref` до компонента не доходит вовсе: оно вынимается из
 * набора свойств ещё до вызова. Это молчаливая потеря — ни предупреждения, ни ошибки; блок
 * просто не прокручивается, и выглядит это как «переход не работает». Ровно так и вышло
 * 22.09.2026 с переходом «Объекты с недобором»: код был написан, а на экране не происходило
 * ничего, потому что до `<section>` ссылка не добиралась.
 *
 * Нужна она тому, кто приводит к блоку человека по переходу «Подробно»
 * (`shared/ui/block/фокус`). Самому блоку — ни к чему: он не прокручивает себя сам.
 */
export const Block = forwardRef<HTMLElement, BlockProps>(function Block(
  { title, icon: Значок, note, action, flush, children, className },
  ref,
) {
  return (
    <section
      ref={ref}
      className={cn(
        // `shrink-0` обязателен. Блоки лежат в колонке с прокруткой, а гибкий элемент в
        // колонке по умолчанию сжимается: без запрета блок ужимался до высоты, которая
        // осталась, и вместе со скруглением обрезал собственное содержимое — на живом экране
        // было видно половину первой строки списка и обрезанный значок.
        //
        // Скругление, тень и рамка — как у блока AlignUI (`widget-box`): 16 точек радиуса,
        // тень в один пиксель почти прозрачным чёрным. Тень здесь не украшение: без неё белый
        // блок на светло-сером поле держится только рамкой, и на плохом офисном мониторе
        // граница пропадает вовсе — блоки сливаются в одно белое полотно.
        'shrink-0 overflow-hidden rounded-2xl border border-stroke-soft-200 bg-bg-white-0 shadow-regular-xs',
        className,
      )}
    >
      <header className='flex min-h-12 items-center justify-between gap-4 border-b border-stroke-soft-200 px-5 py-3'>
        <div className='flex min-w-0 items-center gap-3'>
          {Значок !== undefined && (
            <Значок aria-hidden className='size-5 shrink-0 text-text-sub-600' />
          )}
          <div className='flex min-w-0 flex-col'>
            <h2 className='truncate text-label-sm text-text-strong-950'>{title}</h2>
            {/* Пояснение под заголовком читается на 7:1, а не на 2,6:1, как бледный токен:
                «с мая 2026 по октябрь 2026» над итогом назвало период, который человеку нужен. */}
            {note !== undefined && (
              <span className='truncate text-paragraph-xs text-text-sub-600'>{note}</span>
            )}
          </div>
        </div>
        {action !== undefined && <div className='flex shrink-0 items-center gap-2'>{action}</div>}
      </header>
      <div className={flush === true ? undefined : 'p-5'}>{children}</div>
    </section>
  );
});
